import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Plus, Users } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { contactsService, ContactGroupChip } from '@/services/contacts.service';
import { contactGroupsService, ContactGroup } from '@/services/contact-groups.service';

type Target = { contactUid: string; groups?: ContactGroupChip[] };

/** Checkbox that can show "some of the selection is in this group". */
function TriCheckbox({ state, onChange }: { state: 'on' | 'off' | 'mixed'; onChange: () => void }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = state === 'mixed'; }, [state]);
  return <input ref={ref} type="checkbox" checked={state === 'on'} onChange={onChange} className="h-4 w-4 rounded border-border accent-primary" />;
}

/**
 * Add / remove one or many contacts to groups in one place. Only the groups
 * whose box changed are written, so a partial selection ("mixed") is left
 * alone unless the user explicitly ticks or clears it.
 */
export function ContactGroupsModal({
  open, onClose, targets, groups, onGroupCreated, onSaved,
}: {
  open: boolean;
  onClose: () => void;
  targets: Target[];
  groups: ContactGroup[];
  onGroupCreated?: (g: ContactGroup) => void;
  onSaved: () => void;
}) {
  const initial = useMemo(() => {
    const m = new Map<string, 'on' | 'off' | 'mixed'>();
    for (const g of groups) {
      const n = targets.filter((t) => t.groups?.some((x) => x.groupUid === g.groupUid)).length;
      m.set(g.groupUid, n === 0 ? 'off' : n === targets.length ? 'on' : 'mixed');
    }
    return m;
  }, [groups, targets]);
  // Only the user's own ticks are stored, so groups that load (or props that
  // re-render) after opening never read as a change.
  const [overrides, setOverrides] = useState<Map<string, 'on' | 'off'>>(new Map());
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setOverrides(new Map()); setNewName(''); } }, [open]);
  const stateOf = (uid: string) => overrides.get(uid) ?? initial.get(uid) ?? 'off';

  const toggle = (uid: string) => setOverrides((prev) => new Map(prev).set(uid, stateOf(uid) === 'on' ? 'off' : 'on'));

  const createGroup = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const g = await contactGroupsService.create({ name });
      onGroupCreated?.(g);
      setOverrides((prev) => new Map(prev).set(g.groupUid, 'on'));
      setNewName('');
    } catch { /* interceptor toasts */ }
  };

  const changed = groups.filter((g) => overrides.has(g.groupUid) && overrides.get(g.groupUid) !== (initial.get(g.groupUid) ?? 'off'));

  const save = async () => {
    const uids = targets.map((t) => t.contactUid);
    setBusy(true);
    try {
      for (const g of changed) {
        if (overrides.get(g.groupUid) === 'on') await contactsService.bulkAddToGroup(uids, g.groupUid);
        else await contactsService.bulkRemoveFromGroup(uids, g.groupUid);
      }
      toast.success(changed.length ? 'Groups updated' : 'No changes');
      onSaved();
      onClose();
    } catch { /* interceptor toasts */ }
    finally { setBusy(false); }
  };

  const who = targets.length === 1 ? 'this contact' : `${targets.length} contacts`;

  return (
    <Modal open={open} onClose={onClose} title={`Groups for ${who}`}>
      {groups.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-6 text-text-muted">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-alt"><Users size={16} /></div>
          <span className="text-sm">No groups yet — create the first one below.</span>
        </div>
      ) : (
        <div className="max-h-72 divide-y divide-border overflow-y-auto rounded-xl border border-border">
          {groups.map((g) => {
            const s = stateOf(g.groupUid);
            return (
              <label key={g.groupUid} className="flex cursor-pointer items-center gap-3 px-3.5 py-2.5 transition-colors hover:bg-surface-alt/60">
                <TriCheckbox state={s} onChange={() => toggle(g.groupUid)} />
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: g.color }} />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">{g.name}</span>
                {s === 'mixed' && <span className="text-2xs text-text-muted">some selected</span>}
                <span className="text-2xs tabular-nums text-text-muted">{g.contactCount}</span>
              </label>
            );
          })}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createGroup(); } }}
            placeholder="New group name"
          />
        </div>
        <Button variant="secondary" iconLeft={<Plus size={14} />} disabled={!newName.trim()} onClick={createGroup}>Create</Button>
      </div>

      <div className="mt-5 flex items-center justify-between gap-2">
        <span className={clsx('text-xs', changed.length ? 'text-text-secondary' : 'text-text-muted')}>
          {changed.length ? `${changed.length} group${changed.length === 1 ? '' : 's'} will change` : 'Tick a group to add, untick to remove'}
        </span>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={busy} disabled={!changed.length} onClick={save}>Save</Button>
        </div>
      </div>
    </Modal>
  );
}
